import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { login } from '../../redux/authSlice';
import { useHistory, Link } from 'react-router-dom';
import logoImg from "../../assets/Component 1.svg";
import Loading from '../../component/Loading/Loading';
import { API_URL } from '../../services/apiconfig';
import { useSelector } from 'react-redux';
import { Eye, EyeOff, LogIn, Mail, Lock } from "lucide-react";
import AlertMsg from '../../component/AlertMsg/AlertMsg';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });

  const dispatch = useDispatch();
  const history = useHistory();
  const token = useSelector((state) => state.auth.token);

  const handleLogin = async () => {
    // Validate form
    if (!username.trim()) {
      setError('Email is required');
      return;
    }
    if (!password) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/login-api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emailId: username, password })
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const data = await response.json();
      dispatch(login(data));
      setAlert({ visible: true, message: 'Login successful!', type: 'success' });

      // Redirect after short delay to show success message
      setTimeout(() => {
          history.push('/main#');
          // window.location.reload();
      }, 1000);

    } catch (error) {
      setError('Invalid username or password. Please try again.');
      setAlert({ visible: true, message: 'Login failed. Please check your credentials.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="bg-gray-50 min-h-screen md:p-4 flex items-center justify-center">
      {loading && <Loading />}

      <div className="max-w-7xl mx-auto">
        {alert.visible && (
          <AlertMsg
            message={alert.message}
            type={alert.type}
            onClose={() => setAlert({ ...alert, visible: false })}
          />
        )}

        <div className="flex flex-col md:flex-row shadow-lg rounded-lg overflow-hidden">
          {/* Left side - Login Form */}
          <div className="w-full md:w-1/2 bg-white p-8">
            <div className="max-w-md mx-auto">
              <div className="flex justify-center mb-8">
                <img src={logoImg} alt="Company Logo" className="h-16 scale-150" />
              </div>

              <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">Welcome Back</h1>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full rounded-md border border-gray-300 pl-10 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div className="mb-6">
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <Link to={'/forgot-password'} className="text-sm text-blue-500 hover:text-blue-700">
                    Forgot Password?
                  </Link>
                </div>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock size={18} className="text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full rounded-md border border-gray-300 pl-10 pr-10 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your password"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleLogin}
                disabled={loading}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md flex items-center justify-center gap-2 transition duration-200"
              >
                <LogIn size={18} />
                <span>Sign In</span>
              </button>
            </div>
          </div>

          {/* Right side - Image/Banner */}
          <div className="hidden md:block w-1/2 bg-blue-500">
            <div className="h-full flex items-center justify-center p-12">
              <div className="text-white max-w-md">
                <h2 className="text-3xl font-bold mb-6">Community Management Portal</h2>
                <p className="text-lg mb-6">
                  Log in to access your community management dashboard and tools for efficient property administration.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-white text-blue-500 flex items-center justify-center font-bold">✓</span>
                    <span>Manage amenities and facilities</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-white text-blue-500 flex items-center justify-center font-bold">✓</span>
                    <span>Track payments and due dates</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-white text-blue-500 flex items-center justify-center font-bold">✓</span>
                    <span>Community announcements and events</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;